import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, SectionCard, EmptyState, ConfirmDelete, Loader, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, dateTimeFmt } from "../lib/api";

const BROWSERS = [["Edg/", "Edge"], ["OPR/", "Opera"], ["SamsungBrowser", "Samsung Internet"], ["Firefox/", "Firefox"], ["Chrome/", "Chrome"], ["Safari/", "Safari"]];
const SYSTEMS = [["Android", "Android"], ["iPhone", "iPhone"], ["iPad", "iPad"], ["Windows", "Windows"], ["Mac OS X", "Mac"], ["Linux", "Linux"]];
const ROLES = { admin: "Admin", employee: "Employee", truck_manager: "Truck manager" };
const FILTERS = [["all", "Everyone"], ["owner", "Owner account"], ["staff", "Staff"], ["off_hours", "Outside business hours"]];

export function deviceLabel(ua = "") {
  const b = BROWSERS.find(([k]) => ua.includes(k));
  const s = SYSTEMS.find(([k]) => ua.includes(k));
  if (!b && !s) return ua || "Unknown device";
  return `${b ? b[1] : "Browser"} on ${s ? s[1] : "unknown system"}`;
}

const Tag = ({ tone, children, testid }) => (
  <span className={`mr-1 rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tone}`} data-testid={testid}>{children}</span>
);

function DevicesCard() {
  const { logout } = useAuth();
  const { data, reload } = useApi("/auth/devices");
  const remove = async (d) => {
    try {
      await api.delete(`/auth/devices/${d.id}`);
      toast.success("Device removed and signed out");
      if (d.is_current) logout(); else reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <SectionCard title="Your devices" testid="devices-card">
      {!data ? <Loader /> : !data.length ? <EmptyState text="No remembered devices" testid="devices-empty" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="devices-table">
            <thead className={TABLE_HEAD}><tr><th className="px-4 py-2 text-left">Device</th><th className="px-4 py-2 text-left">IP</th><th className="px-4 py-2 text-left">First seen</th><th className="px-4 py-2 text-left">Last sign-in</th><th /></tr></thead>
            <tbody>{data.map((d) => (
              <tr key={d.id} className={TABLE_ROW} data-testid={`device-row-${d.id}`}>
                <td className="px-4 py-2"><span className="font-medium">{deviceLabel(d.user_agent)}</span> {d.is_current && <Tag tone="border-emerald-500/40 text-emerald-400" testid="device-current-badge">This device</Tag>}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{d.ip || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(d.first_seen)}</td>
                <td className="px-4 py-2 text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(d.last_seen)}</td>
                <td className="px-2 text-right"><ConfirmDelete title={`Remove ${deviceLabel(d.user_agent)}?`} testid={`remove-device-${d.id}`} onConfirm={() => remove(d)}
                  description={d.is_current ? "This is the device you are using now. You will be signed out straight away." : "It will be signed out straight away. Signing in from it again will send you a new-device alert."} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function SignInsCard() {
  const [filter, setFilter] = useState("all");
  const { data } = useApi("/auth/signins", { filter });
  const picker = (
    <Select value={filter} onValueChange={setFilter}>
      <SelectTrigger className="h-9 w-[200px] bg-slate-950" data-testid="signins-filter-select"><SelectValue /></SelectTrigger>
      <SelectContent>{FILTERS.map(([k, label]) => <SelectItem key={k} value={k} data-testid={`signins-filter-${k}`}>{label}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <SectionCard title="Recent sign-ins" right={picker} testid="signins-card">
      {!data ? <Loader /> : !data.length ? <EmptyState text="No sign-ins for this filter" testid="signins-empty" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="signins-table">
            <thead className={TABLE_HEAD}><tr><th className="px-4 py-2 text-left">When</th><th className="px-4 py-2 text-left">Person</th><th className="px-4 py-2 text-left">Device</th><th className="px-4 py-2 text-left">IP</th><th className="px-4 py-2 text-left">Flags</th></tr></thead>
            <tbody>{data.map((s) => (
              <tr key={s.id} className={TABLE_ROW} data-testid={`signin-row-${s.id}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(s.created_at)}</td>
                <td className="px-4 py-2"><div className="font-medium">{s.name}</div><div className="text-xs text-slate-500">{s.email} · {ROLES[s.role] || s.role}</div></td>
                <td className="px-4 py-2 text-slate-300">{deviceLabel(s.user_agent)}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{s.ip || "—"}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {s.new_device && <Tag tone="border-amber-500/40 text-amber-400" testid={`signin-new-device-${s.id}`}>New device</Tag>}
                  {s.off_hours && <Tag tone="border-rose-500/40 text-rose-400" testid={`signin-off-hours-${s.id}`}>Outside hours</Tag>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function DisablePrompt() {
  const [params, setParams] = useSearchParams();
  const id = params.get("disable");
  const { data, reload } = useApi(id ? "/users" : null);
  const [busy, setBusy] = useState(false);
  if (!id || !data) return null;
  const u = data.find((x) => x.id === id);
  const disable = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${id}`, { active: false });
      toast.success(`${u.name} disabled and signed out of every device`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rise rounded-lg border border-rose-500/40 bg-rose-500/10 p-5" data-testid="disable-prompt">
      {!u ? <p className="text-sm" data-testid="disable-prompt-missing">That account no longer exists.</p>
        : !u.active ? <p className="text-sm" data-testid="disable-prompt-done"><b>{u.name}</b> is disabled. You can turn the account back on from Users.</p> : (
          <>
            <div className="font-display text-lg font-bold uppercase" data-testid="disable-prompt-title">Disable {u.name}?</div>
            <p className="mt-1 text-sm text-slate-300">{u.email} · {ROLES[u.role] || u.role}. They will be signed out of every device straight away and cannot sign in until you turn the account back on in Users.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={disable} disabled={busy} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="disable-account-confirm-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Disable account
              </Button>
              <Button variant="outline" className="border-slate-700" onClick={() => setParams({})} data-testid="disable-account-dismiss-button">Keep active</Button>
            </div>
          </>
        )}
    </div>
  );
}

export default function SignIns() {
  const { user } = useAuth();
  if (!user.is_owner) return <EmptyState text="Only the owner can view sign-in history" testid="signins-owner-only" />;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Owner · Security" title="Sign-in history"
        subtitle="Devices remembered for your account and every sign-in from the last 90 days. Staff sign-ins outside Monday to Saturday, 07:00 to 19:00 (Tanzania time) are flagged and emailed to you." />
      <DisablePrompt />
      <DevicesCard />
      <SignInsCard />
    </div>
  );
}
